// ZEASTRO In-App User Manual and shared discovery taxonomy for web and mobile.
// Naming conventions: display "Cosmic Pro" (never "Celestial").

export interface ManualStep {
  text: string;
}

export interface ManualSection {
  id: string;
  title: string;
  summary: string;
  steps: ManualStep[];
  mobileNote?: string; // extra context specific to mobile flows (omitted on web)
  webNote?: string;    // extra context specific to web flows (omitted on mobile)
}

export interface ManualTopic {
  id: string;
  title: string;
  icon: string;        // Font Awesome class used by the web app
  mobileIcon: string;  // Feather icon name used by the mobile app
  sections: ManualSection[];
}

export type ExplorePlatform = "web" | "mobile";
export type ExplorePlanTier = "free" | "cosmic" | "celestial";

export interface ExplorePlanStatus {
  status: "included" | "upgrade";
  planLabel: string;
}

const EXPLORE_PLAN_RANK: Record<ExplorePlanTier, number> = {
  free: 0,
  cosmic: 1,
  celestial: 2,
};

function normalizeExplorePlanTier(value: string): ExplorePlanTier | null {
  switch (value.trim().toLowerCase()) {
    case "free":
      return "free";
    case "cosmic":
    case "session":
      return "cosmic";
    case "celestial":
    case "enterprise":
    case "premium":
      return "celestial";
    default:
      return null;
  }
}

export function getExplorePlanStatus(
  featureTiers: ExplorePlanTier[] | undefined,
  currentTier: string | null | undefined,
): ExplorePlanStatus | null {
  if (!featureTiers?.length || !currentTier) return null;
  const canonicalCurrentTier = normalizeExplorePlanTier(currentTier);
  if (!canonicalCurrentTier) return null;
  const currentRank = EXPLORE_PLAN_RANK[canonicalCurrentTier];
  const normalizedFeatureTiers = featureTiers
    .map((tier) => normalizeExplorePlanTier(tier))
    .filter((tier): tier is ExplorePlanTier => tier !== null);
  const nextTier = normalizedFeatureTiers
    .filter((tier) => EXPLORE_PLAN_RANK[tier] > currentRank)
    .sort((left, right) => EXPLORE_PLAN_RANK[left] - EXPLORE_PLAN_RANK[right])[0];
  const includedTier = normalizedFeatureTiers
    .filter((tier) => EXPLORE_PLAN_RANK[tier] <= currentRank)
    .sort((left, right) => EXPLORE_PLAN_RANK[right] - EXPLORE_PLAN_RANK[left])[0];
  const displayedTier = nextTier ?? includedTier ?? canonicalCurrentTier;

  return {
    status: nextTier ? "upgrade" : "included",
    planLabel:
      displayedTier === "celestial"
        ? "Cosmic Pro"
        : displayedTier === "cosmic"
          ? "Cosmic"
          : "Free",
  };
}

/** Sidebar inclusion is separate from tiers that add extra feature benefits. */
export function isExploreFeatureIncludedInPlan(
  feature: { minimumTier?: ExplorePlanTier },
  currentTier: string | null | undefined,
): boolean {
  // Until effective coverage loads, show only the universally available tier.
  const tier = normalizeExplorePlanTier(currentTier ?? "") ?? "free";
  return EXPLORE_PLAN_RANK[tier] >= EXPLORE_PLAN_RANK[feature.minimumTier ?? "free"];
}

export interface ExploreDestination {
  path: string;
  /** Optional platform-specific display name (for example, My Kundali). */
  label?: string;
  /** Optional platform-specific icon token when the icon library differs. */
  icon?: string;
}

export interface ExploreFeatureDefinition {
  id: string;
  label: string;
  description: string;
  /** Semantic icon token mapped to the platform's icon library by each client. */
  icon: string;
  labelKey: string;
  /** Plans that add subscription benefits for this feature, in ascending order. */
  planTiers?: ExplorePlanTier[];
  /** First personal plan including this destination; higher plans inherit it.
   * Omitted for universally available destinations. Not an authorization gate. */
  minimumTier?: ExplorePlanTier;
  web?: ExploreDestination;
  mobile?: ExploreDestination;
}

export interface ExploreSectionDefinition {
  id: string;
  title: string;
  items: ExploreFeatureDefinition[];
}

export interface ExploreFeature extends ExploreFeatureDefinition {
  path: string;
  label: string;
}

export interface ExploreSection extends Omit<ExploreSectionDefinition, "items"> {
  items: ExploreFeature[];
}

/**
 * The product-wide Explore information architecture.
 *
 * Keep this list ordered: it is the journey users see on Home, Explore, More,
 * and the desktop sidebar. A destination is declared only for platforms that
 * actually have that screen, so consumers cannot accidentally create a dead
 * link for the other platform.
 */
export const EXPLORE_TAXONOMY: ExploreSectionDefinition[] = [
  {
    id: "kundali-compatibility",
    title: "Kundali & Compatibility",
    items: [
      {
        id: "compatibility",
        label: "Compatibility",
        description: "Choose compatibility, marriage, or family analysis",
        icon: "users",
        labelKey: "nav.compatibility",
        planTiers: ["free", "cosmic", "celestial"],
        web: { path: "/compatibility-hub" },
        mobile: { path: "/compatibility-hub" },
      },
      {
        id: "kundali",
        label: "Kundali",
        description: "Birth details, chart & planetary positions",
        icon: "grid",
        labelKey: "nav.kundali",
        planTiers: ["cosmic", "celestial"],
        minimumTier: "cosmic",
        web: { path: "/kundali" },
        mobile: { path: "/kundali", label: "My Kundali" },
      },
    ],
  },
  {
    id: "daily-astrology-timing",
    title: "Daily Astrology & Auspicious Timing",
    items: [
      {
        id: "daily-horoscope",
        label: "Daily Horoscope",
        description: "Your personalised daily horoscope",
        icon: "sun",
        labelKey: "nav.daily_horoscope",
        planTiers: ["free", "cosmic"],
        web: { path: "/daily-horoscope" },
      },
      {
        id: "festivals",
        label: "Festivals",
        description: "Upcoming festivals and observances",
        icon: "calendar",
        labelKey: "nav.festivals",
        web: { path: "/festivals" },
      },
      {
        id: "panchang",
        label: "Panchang",
        description: "Daily tithi, nakshatra & timings",
        icon: "sunrise",
        labelKey: "nav.panchang",
        web: { path: "/panchang" },
        mobile: { path: "/panchang" },
      },
      {
        id: "muhurat",
        label: "Muhurat",
        description: "Auspicious windows for the day",
        icon: "clock",
        labelKey: "nav.muhurat",
        web: { path: "/muhurat" },
        mobile: { path: "/muhurat" },
      },
    ],
  },
  {
    id: "guidance-remedies",
    title: "Guidance & Remedies",
    items: [
      {
        id: "remedies-alerts",
        label: "Remedies & Alerts",
        description: "Preventive warnings & your remedy plan",
        icon: "shield",
        labelKey: "nav.guidance",
        planTiers: ["cosmic"],
        minimumTier: "cosmic",
        web: { path: "/guidance" },
        mobile: { path: "/guidance" },
      },
      {
        id: "gemstones",
        label: "Gemstones",
        description: "Main gemstone and two budget alternatives",
        icon: "hexagon",
        labelKey: "nav.gemstones",
        planTiers: ["free", "cosmic"],
        web: { path: "/gemstones" },
        mobile: { path: "/gemstones" },
      },
    ],
  },
  {
    id: "self-discovery-divination",
    title: "Self-Discovery & Divination",
    items: [
      {
        id: "numerology",
        label: "Numerology",
        description: "Your core numbers & meaning",
        icon: "hash",
        labelKey: "nav.numerology",
        planTiers: ["cosmic"],
        minimumTier: "cosmic",
        web: { path: "/numerology" },
        mobile: { path: "/numerology" },
      },
      {
        id: "tarot",
        label: "Tarot Reading",
        description: "Draw cards for guidance",
        icon: "layers",
        labelKey: "nav.tarot",
        planTiers: ["cosmic", "celestial"],
        minimumTier: "cosmic",
        web: { path: "/tarot" },
        mobile: { path: "/tarot" },
      },
      {
        id: "palm-reading",
        label: "Palm Reading",
        description: "AI analysis of your palm lines",
        icon: "aperture",
        labelKey: "nav.palm_reading",
        planTiers: ["free"],
        web: { path: "/palm-reading" },
        mobile: { path: "/palm-reading" },
      },
      {
        id: "cosmic-avatar",
        label: "Cosmic Avatar",
        description: "Your zodiac identity, visualized",
        icon: "image",
        labelKey: "nav.cosmic_avatar",
        web: { path: "/ai-avatar" },
        mobile: { path: "/avatar" },
      },
    ],
  },
  {
    id: "relationships-wellness-growth",
    title: "Relationships, Wellness & Growth",
    items: [
      {
        id: "relationship-coach",
        label: "Relationship Coach",
        description: "Chat with an AI coach",
        icon: "message-circle",
        labelKey: "nav.relationship_coach",
        planTiers: ["celestial"],
        minimumTier: "celestial",
        web: { path: "/relationship-coach" },
        mobile: { path: "/relationship-coach" },
      },
      {
        id: "relationship-journal",
        label: "Relationship Journal",
        description: "Record moods & reflections",
        icon: "edit-3",
        labelKey: "nav.relationship_journal",
        web: { path: "/relationship-journal" },
        mobile: { path: "/relationship-journal" },
      },
      {
        id: "wellness",
        label: "Wellness",
        description: "Daily nutrition & routine tips",
        icon: "heart",
        labelKey: "nav.wellness",
        web: { path: "/wellness" },
        mobile: { path: "/wellness" },
      },
      {
        id: "cosmic-nutrition",
        label: "Cosmic Nutrition",
        description: "Foods & meal plans for your sign",
        icon: "coffee",
        labelKey: "nav.cosmic_nutrition",
        web: { path: "/cosmic-nutrition" },
        mobile: { path: "/cosmic-nutrition" },
      },
      {
        id: "daily-growth",
        label: "Daily Growth",
        description: "Log progress and track improvements",
        icon: "trending-up",
        labelKey: "nav.daily_growth",
        web: { path: "/daily-growth" },
        mobile: { path: "/daily-growth" },
      },
      {
        id: "cosmic-challenges",
        label: "Cosmic Challenges",
        description: "Gamified astrology challenges",
        icon: "award",
        labelKey: "nav.cosmic_challenges",
        web: { path: "/cosmic-challenges" },
        mobile: { path: "/cosmic-challenges" },
      },
    ],
  },
  {
    id: "learn-cosmic-science",
    title: "Learn & Cosmic Science",
    items: [
      {
        id: "astro-learn",
        label: "AstroLearn",
        description: "Understand every ZEASTRO tool with AI",
        icon: "book-open",
        labelKey: "nav.astro_learn",
        web: { path: "/astro-learn" },
        mobile: { path: "/astro-learn" },
      },
      {
        id: "cosmic-science",
        label: "Cosmic Science",
        description: "Explore astrology topics with AI",
        icon: "telescope",
        labelKey: "nav.cosmic_science",
        planTiers: ["free"],
        web: { path: "/cosmic-science" },
        mobile: { path: "/cosmic-science", icon: "book-open" },
      },
    ],
  },
  {
    id: "account-professional-tools",
    title: "Account & Professional Tools",
    items: [
      {
        id: "edit-profile",
        label: "Edit Profile",
        description: "Update your personal details",
        icon: "edit-3",
        labelKey: "nav.edit_profile",
      },
      {
        id: "notifications",
        label: "Notifications",
        description: "Your alerts & updates",
        icon: "bell",
        labelKey: "nav.notifications",
        web: { path: "/notifications" },
      },
      {
        id: "plans-pricing",
        label: "Plans & Pricing",
        description: "Compare subscription tiers",
        icon: "zap",
        labelKey: "nav.plans_pricing",
        web: { path: "/pricing" },
      },
      {
        id: "astrologer-dashboard",
        label: "Astrologer Dashboard",
        description: "Manage your sessions & availability",
        icon: "briefcase",
        labelKey: "nav.astrologer_dashboard",
        web: { path: "/astrologer-dashboard" },
        mobile: { path: "/astrologer-dashboard" },
      },
      {
        id: "astrologers",
        label: "Astrologers",
        description: "Browse professional astrologers",
        icon: "star",
        labelKey: "nav.marketplace",
        planTiers: ["cosmic", "celestial"],
        minimumTier: "cosmic",
        // Astrologers is a primary mobile tab, so it is intentionally not
        // exposed through mobile Explore. The web route remains discoverable.
        web: { path: "/marketplace" },
      },
    ],
  },
];

/** Resolve the ordered, valid destinations for one client. */
export function getExploreSections(platform: ExplorePlatform): ExploreSection[] {
  return EXPLORE_TAXONOMY.map(({ items, ...section }) => ({
    ...section,
    items: items.flatMap((item) => {
      const destination = item[platform];
      if (!destination) return [];
      return [
        {
          ...item,
          ...destination,
          label: destination.label ?? item.label,
          icon: destination.icon ?? item.icon,
        },
      ];
    }),
  }));
}

export const MANUAL_TOPICS: ManualTopic[] = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: "fa-rocket",
    mobileIcon: "zap",
    sections: [
      {
        id: "what-is-zeastro",
        title: "What is ZEASTRO?",
        summary:
          "ZEASTRO is an AI-powered astrology platform combining Vedic and western traditions. It offers birth charts (Kundali), compatibility analysis, daily horoscopes, tarot and palm readings, and live sessions with professional astrologers.",
        steps: [
          { text: "Open the app or visit zeastro.com." },
          { text: "Browse features as a guest, or sign up for a free account to save your data." },
          { text: "Enter your birth details once — ZEASTRO uses them for personalised readings across all features." },
        ],
      },
      {
        id: "create-account",
        title: "Creating an Account",
        summary:
          "A free account lets you save birth data, chart history, and preferences across sessions.",
        steps: [
          { text: "Tap or click Sign Up and enter your email and a password (minimum 8 characters)." },
          { text: "Optionally add your first and last name and a phone number." },
          { text: "Check your inbox and verify your email if prompted." },
          { text: "You can also continue as a Guest to explore without committing — your data will not be saved." },
        ],
        mobileNote: "On mobile, sign up is on the Profile tab.",
        webNote: "On the web, sign up is available at /signup or from the landing page.",
      },
      {
        id: "sign-in",
        title: "Signing In",
        summary: "Sign in to access your saved charts, sessions, and subscription benefits.",
        steps: [
          { text: "Open the Sign In page and enter your email and password." },
          { text: "If you have forgotten your password, tap Forgot password? and follow the reset link sent to your email." },
          { text: "After signing in you are returned to your home dashboard." },
        ],
        mobileNote: "Sign in is on the Profile tab. Tap 'Forgot password?' to reset.",
        webNote: "Navigate to /login. Use /forgot-password if you need a reset link.",
      },
    ],
  },
  {
    id: "profile-birth-data",
    title: "Profile & Birth Data",
    icon: "fa-user-circle",
    mobileIcon: "user",
    sections: [
      {
        id: "edit-profile",
        title: "Editing Your Profile",
        summary:
          "Your profile holds your name, email, language preference, and theme setting.",
        steps: [
          { text: "Navigate to your Profile page." },
          { text: "Update your first name, last name, or phone number." },
          { text: "Choose your preferred display language — ZEASTRO will translate the interface automatically." },
          { text: "Switch between Light, Dark, or System themes." },
          { text: "Save each section individually." },
        ],
        mobileNote: "Profile is the last tab (person icon). Scroll down for language and theme pickers.",
        webNote: "Profile is in the sidebar (bottom) or at /profile.",
      },
      {
        id: "birth-data",
        title: "Adding Your Birth Details",
        summary:
          "Accurate birth date, time, and place are essential for Kundali, horoscopes, and Vedic features.",
        steps: [
          { text: "Go to Profile > Birth Details (or the Birth Details screen on mobile)." },
          { text: "Enter your date of birth, time of birth (as precise as possible), and city of birth." },
          { text: "ZEASTRO resolves the coordinates and timezone automatically." },
          { text: "Save — your Kundali and charts will update immediately." },
        ],
        mobileNote: "On mobile, find Birth Details in Explore > Account > Birth details.",
        webNote: "On web, birth data is on the Profile page.",
      },
      {
        id: "astrology-tradition",
        title: "Choosing Your Astrology Tradition",
        summary:
          "ZEASTRO supports regional astrology traditions — your selection drives the compatibility system, festival calendar, deity names, and AI guidance style.",
        steps: [
          { text: "Go to Profile > Astrology Tradition." },
          { text: "Pick your tradition (e.g., South Indian, North Indian, Tamil, Western)." },
          { text: "Save. Daily horoscopes, Muhurat recommendations, and compatibility analysis will all reflect your tradition." },
        ],
      },
    ],
  },
  {
    id: "kundali-charts",
    title: "Kundali & Charts",
    icon: "fa-circle-nodes",
    mobileIcon: "grid",
    sections: [
      {
        id: "view-kundali",
        title: "Viewing Your Kundali",
        summary:
          "Your Kundali (Vedic birth chart) shows planetary positions, houses, nakshatra, and dasha periods at the moment you were born.",
        steps: [
          { text: "Navigate to Kundali (sidebar on web, or Explore > My Kundali on mobile)." },
          { text: "If you haven't added birth details yet, you will be prompted to do so." },
          { text: "The chart displays your lagna (ascendant), planetary placements, and active dasha." },
          { text: "Scroll down for an AI interpretation of key placements and their life implications." },
        ],
      },
      {
        id: "numerology",
        title: "Numerology Report",
        summary:
          "Numerology derives your Life Path, Destiny, and Soul Urge numbers from your birth date and name.",
        steps: [
          { text: "Go to Numerology from the sidebar or Explore." },
          { text: "Confirm your birth details and full name." },
          { text: "Read your core numbers and their interpretations." },
        ],
      },
      {
        id: "panchang-muhurat",
        title: "Panchang & Muhurat",
        summary:
          "Panchang shows the daily Vedic almanac — tithi, nakshatra, yoga, karana, and vara. Muhurat highlights auspicious time windows for important activities.",
        steps: [
          { text: "Open Panchang to see today's Vedic calendar details." },
          { text: "Open Muhurat to find the best windows for activities like travel, signing contracts, or starting a new project." },
          { text: "Select a date using the date picker to see future or past panchang data." },
        ],
      },
    ],
  },
  {
    id: "compatibility-milan",
    title: "Compatibility & Milan",
    icon: "fa-heart",
    mobileIcon: "heart",
    sections: [
      {
        id: "compatibility-check",
        title: "Running a Compatibility Check",
        summary:
          "Compare two zodiac signs or full birth charts to understand relationship dynamics.",
        steps: [
          { text: "Go to Compatibility." },
          { text: "Enter details for both people (or select saved profiles)." },
          { text: "Choose Western (sun-sign) or Vedic (Ashtakoot/Porutham) analysis." },
          { text: "Review the score, factor breakdown, and AI narrative." },
        ],
      },
      {
        id: "kundali-milan",
        title: "Kundali Milan (Marriage Match)",
        summary:
          "Kundali Milan compares two full birth charts across Ashtakoot (North Indian) or Porutham (South Indian/Tamil) factors to assess marriage compatibility.",
        steps: [
          { text: "Go to Kundali Match (web sidebar) or Explore > Kundali Match (mobile)." },
          { text: "Enter the birth details of both partners." },
          { text: "Review the factor-by-factor breakdown and total score." },
          { text: "Scroll down for the AI narrative on strengths and areas to work on." },
          { text: "Use the Share button to generate a link you can send to your partner." },
        ],
        mobileNote: "The shared link opens inside the mobile app if the app is installed.",
      },
      {
        id: "find-matches",
        title: "Using the Compatibility Assistant",
        summary: "Ask natural-language questions and explore deterministic compatibility scores.",
        steps: [
          { text: "Go to Compatibility Assistant." },
          { text: "Ask about signs, friendship, relationships, business, or team compatibility." },
          { text: "Review ZEASTRO's calculated scores and ask follow-up questions about the results." },
        ],
      },
      {
        id: "family-team",
        title: "Family & Team Compatibility",
        summary:
          "Analyse harmony within a group — family, friends, or a work team.",
        steps: [
          { text: "Go to Family or Team Compatibility." },
          { text: "Add each member's birth details." },
          { text: "View overall group harmony scores and individual pair insights." },
        ],
        webNote: "Family and Team Compatibility are available on the web.",
      },
    ],
  },
  {
    id: "horoscopes-guidance",
    title: "Daily Guidance & Horoscopes",
    icon: "fa-sun",
    mobileIcon: "sun",
    sections: [
      {
        id: "daily-horoscope",
        title: "Daily Horoscope",
        summary:
          "Your personalised daily horoscope is generated each morning and covers love, career, health, and luck.",
        steps: [
          { text: "Open Daily Horoscope from the home screen or sidebar." },
          { text: "Select a zodiac sign (defaults to yours if your profile is complete)." },
          { text: "Read the overview or tap each category for deeper guidance." },
          { text: "The content respects your chosen astrology tradition." },
        ],
      },
      {
        id: "remedies-alerts",
        title: "Remedies & Alerts",
        summary:
          "AI-generated preventive warnings and remedy suggestions based on your current planetary transits.",
        steps: [
          { text: "Navigate to Remedies & Alerts (Guidance)." },
          { text: "Review active planetary influences and any flagged warnings." },
          { text: "Follow the suggested remedies (mantras, gemstones, dietary changes) to mitigate challenging transits." },
          { text: "Tap any alert to book an astrologer for personalised advice." },
        ],
      },
      {
        id: "gemstones",
        title: "Gemstone Recommendations",
        summary: "See one chart-based main gemstone and two traditionally lower-cost substitutes for the same planet.",
        steps: [
          { text: "Go to Gemstones from the sidebar or Explore." },
          { text: "Review your main gemstone and the two budget alternatives. Choose one option, not all three; prices vary by quality and seller." },
          { text: "Filter by planet to see which stone strengthens that planet's energy." },
          { text: "Read the wearing instructions and cautions." },
        ],
      },
    ],
  },
  {
    id: "tarot-palm",
    title: "Tarot & Palm Reading",
    icon: "fa-hand-sparkles",
    mobileIcon: "layers",
    sections: [
      {
        id: "tarot-reading",
        title: "Tarot Reading",
        summary:
          "Draw one or more cards for a guided tarot reading interpreted by AI with astrological context.",
        steps: [
          { text: "Go to Tarot Reading." },
          { text: "Focus on your question, then tap Draw Card (or draw a spread)." },
          { text: "Read the AI interpretation combining the card's traditional meaning with your chart context." },
          { text: "Ask up to 3 follow-up questions per reading to go deeper." },
          { text: "Your card-of-the-day is saved and re-used if you return the same day." },
        ],
      },
      {
        id: "palm-reading",
        title: "Palm Reading",
        summary:
          "Upload a photo of your palm and receive an AI analysis of your major and minor lines.",
        steps: [
          { text: "Go to Palm Reading." },
          { text: "Tap Upload Palm Photo and select a clear, well-lit image of your dominant hand." },
          { text: "Wait a few seconds for the AI to analyse the heart, head, life, and fate lines." },
          { text: "Read the interpretation. You can also upload a second hand for comparison." },
        ],
      },
    ],
  },
  {
    id: "sessions-bookings",
    title: "Astrologer Sessions & Bookings",
    icon: "fa-calendar-check",
    mobileIcon: "calendar",
    sections: [
      {
        id: "find-astrologer",
        title: "Finding an Astrologer",
        summary: "Browse vetted professional astrologers and filter by speciality, language, or rating.",
        steps: [
          { text: "Go to Astrologers (Marketplace)." },
          { text: "Browse the list or use filters (language, speciality, rating)." },
          { text: "Tap an astrologer's card to view their profile, reviews, and availability." },
        ],
      },
      {
        id: "book-session",
        title: "Booking a Session",
        summary:
          "Sessions can be chat, video, or in-person. Book a slot and pay in advance.",
        steps: [
          { text: "On an astrologer's profile, select Chat, Video, or In-Person." },
          { text: "Pick an available date and time slot." },
          { text: "Complete payment (wallet balance, Stripe card, or Razorpay)." },
          { text: "You will receive a confirmation email. The session appears in your Bookings." },
        ],
        mobileNote: "On mobile, payment is handled via the app's payment flow — no browser redirect needed.",
        webNote: "On web, payment uses Stripe Checkout or Razorpay depending on your region.",
      },
      {
        id: "join-session",
        title: "Joining a Session",
        summary: "Access your booked session at the scheduled time.",
        steps: [
          { text: "Go to Sessions / Bookings." },
          { text: "Find your upcoming session and tap Join when it's time." },
          { text: "For chat sessions, type your messages in the session window." },
          { text: "For video sessions, allow camera and microphone access when prompted." },
        ],
      },
      {
        id: "rate-session",
        title: "Rating a Session",
        summary: "Leave a star rating and review after your session ends.",
        steps: [
          { text: "After a session ends, a rating prompt will appear." },
          { text: "Select 1–5 stars and optionally leave a written review." },
          { text: "Your rating helps other users discover quality astrologers." },
        ],
      },
    ],
  },
  {
    id: "plans-payments",
    title: "Plans & Payments",
    icon: "fa-credit-card",
    mobileIcon: "credit-card",
    sections: [
      {
        id: "plan-overview",
        title: "Subscription Plans",
        summary:
          "ZEASTRO offers a Free tier and Cosmic Pro, which unlocks advanced AI features, unlimited readings, and premium reports.",
        steps: [
          { text: "Go to Pricing (sidebar or Explore > Plans & pricing)." },
          { text: "Compare the Free and Cosmic Pro tiers." },
          { text: "Tap Upgrade to start a monthly or annual subscription." },
          { text: "Your tier is shown on your Profile page." },
        ],
        webNote: "On web, Cosmic Pro can be purchased via Stripe card checkout.",
        mobileNote: "On mobile, Cosmic Pro subscriptions are managed within the app.",
      },
      {
        id: "manage-subscription",
        title: "Managing Your Subscription",
        summary: "View, change, or cancel your subscription at any time.",
        steps: [
          { text: "Go to Profile > Plan & Billing." },
          { text: "View your current plan and renewal date." },
          { text: "To cancel, tap Cancel Subscription — access continues until the end of your billing period." },
          { text: "To change plans, tap Upgrade or Downgrade." },
        ],
      },
      {
        id: "coach-sessions",
        title: "Relationship Coach Pay-Per-Question",
        summary:
          "The Relationship Coach charges per question or per session pass — no subscription required.",
        steps: [
          { text: "Go to Relationship Coach." },
          { text: "Ask a question. You will see the per-question price before confirming." },
          { text: "Alternatively, purchase a 15-, 30-, or 60-minute session pass for a better rate." },
          { text: "Charges are processed via Razorpay (UPI/card) inline." },
        ],
        webNote: "Relationship Coach is available on the web.",
      },
    ],
  },
  {
    id: "notifications-settings",
    title: "Notifications & Settings",
    icon: "fa-bell",
    mobileIcon: "bell",
    sections: [
      {
        id: "notifications",
        title: "Managing Notifications",
        summary:
          "Control which alerts ZEASTRO sends you — horoscopes, session reminders, planetary alerts, and more.",
        steps: [
          { text: "Go to Notifications (bell icon on web, or Explore > Notifications on mobile)." },
          { text: "Toggle individual notification types on or off." },
          { text: "Daily horoscopes are delivered each morning at your local time." },
          { text: "Grant push notification permission when prompted on mobile for timely alerts." },
        ],
      },
      {
        id: "language-settings",
        title: "Language & Translation",
        summary:
          "ZEASTRO translates the interface into your preferred language automatically.",
        steps: [
          { text: "Go to Profile and open the Language picker." },
          { text: "Select your language. The UI updates immediately." },
          { text: "AI-generated content is also translated into your chosen language." },
          { text: "To mark specific text as untranslatable (e.g., proper names), no action is needed — ZEASTRO detects them automatically." },
        ],
      },
      {
        id: "theme",
        title: "Theme (Light / Dark / System)",
        summary: "Switch between a light or dark interface, or follow your device setting.",
        steps: [
          { text: "Go to Profile and find the Theme picker." },
          { text: "Select Light, Dark, or System." },
          { text: "The change applies instantly across all screens." },
        ],
      },
    ],
  },
];
